import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useMemo, useRef } from "react";
import { aggridHeaderClass, AggridSwitchButton, aggridSwitchButtonWrapperStyle } from "src/components/common/common";

import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell  } from "src/utils/grid/gridStyleUtils";
import { formatDate, validateString } from "src/utils/string/stringUtil";

import { GridCalendar } from "src/components/common/form/GridCalendar";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { createCalendarCellClickHandler, CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, RemoveRowHandler, postSortHandler, useCellChangeHandler, useRowSelectionHandler, getSwitchButtonColumnOptions, createCellCssClassRules, GridCellConfig } from "src/utils/grid/GridHandlerContent";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { SYS4100_R01Dto } from "./Dto_SYS4100_R01";
import { useSYS4100_R01Store, useSYS4100_R01Store_GridCalendar } from "./Store_SYS4100_R01";
import { useSYS4100_R01CellChange } from "./hooks/SYS4100_R01CellChange";
import { useSYS4100_R01Modal } from "./hooks/useSYS4100_R01Modal";
import { headerNames_SYS4100_R01 } from "./Util_SYS4100_R01";
import { showWarning } from "src/utils/toast/toastUtil";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { cellDateComparator } from 'src/utils/grid/gridFunctionUtil';
import { cellStringComparator } from "src/utils/grid/gridFunctionUtil";


const COLUMN_STATE_KEY = 'SYS4100_R01-grid-column-state';

/**
 * 사용자 관리 테이블 컴포넌트
 * 사용자 정보를 표시하고 관리하는 테이블
 */

interface Props {
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<SYS4100_R01Dto> | null>;
	defaultSYS4100_R01Row: SYS4100_R01Dto;
	handleInsertClick: (e: React.MouseEvent, rowIndex?: number) => void;
}

export const Table_SYS4100_R01 = (props: Props) => {
	const { clickable, setClickable, gridRef, defaultSYS4100_R01Row, handleInsertClick } = props;

	const {
		SYS4100_R01List, setSYS4100_R01List,
		removeSYS4100_R01List, updateSYS4100_R01List,
		deletedSYS4100_R01List, addDeletedSYS4100_R01List,
		selectedSYS4100_R01List, addSelectedSYS4100_R01List, removeSelectedSYS4100_R01List,
		initSelectedSYS4100_R01List, allSelectedSYS4100_R01List,
		originalSYS4100_R01List,
		columnState, setColumnState,
		scrollTop, setScrollTop,
	} = useSYS4100_R01Store();
	const { SYS4100_R01GridCalendarState, setSYS4100_R01GridCalendarState } = useSYS4100_R01Store_GridCalendar();
	const { pickerVisible, pickerPosition, targetRowIndex, targetField, targetDate } = SYS4100_R01GridCalendarState;

	const gridWrapperRef = useRef<HTMLDivElement>(null);
	const isRowDraggingRef = useRef(false);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
		handleSwitchButtonHandler,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: SYS4100_R01List,
		defaultGridData: defaultSYS4100_R01Row,
		selectedRow: selectedSYS4100_R01List,
		originalGridData: originalSYS4100_R01List,
		updateRow: (index: number, data: any) => updateSYS4100_R01List(index, data),
	});

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['orgAdminNm', 'upOrgNm', 'mergeOrgNm', ],
		insertedDisabledCells: ['orgAdminNm', 'upOrgNm', ],
		commonDisabledCells: ['orgCd', 'createDt', ],
		
		pointerCells: ['orgNm', 'upOrgCd', 'upOrgNm', 'displaySeq','orgAdmin', 'orgAdminNm', 'dueDt', 'mergeOrgCd', 'mergeOrgNm', 'useYn', 'topOrganiza', ],
		insertedPointerCells: ['orgCd', 'createDt', ],
		commonPointerCells: [''],
		
		alignLeftCells: ['orgCd', 'orgAdmin']
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<SYS4100_R01Dto>({
            idKey: 'valueIndex',
            selectedList: selectedSYS4100_R01List,
            deletedList: deletedSYS4100_R01List,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [selectedSYS4100_R01List, deletedSYS4100_R01List, disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, createCellClassRules]);

	// 컬럼 클릭 액션 설정, 모달 이 있다면 모달 켜는 함수도 의존성 설정을 해줘야 한다
	const commonInputColumns = [ 'orgCd', 'orgNm', 'upOrgCd', 'displaySeq', 'orgAdmin', 'mergeOrgCd', ];

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = ['orgCd', 'orgNm', 'displaySeq', 'orgAdmin', 'mergeOrgCd', ];

	const baseColumnDefs: ColDef<SYS4100_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			(params: any) => renderIndexCell(
				params, 
				(innerParams: SYS4100_R01Dto) => getRowStyleFlags (
					innerParams,
					{
						deletedList: deletedSYS4100_R01List,
						selectedList: selectedSYS4100_R01List,
						dataList: SYS4100_R01List
					}),
				(rowIndex: number, e: React.MouseEvent) => {
					if (e.ctrlKey) {
						for (let i = 0; i < 10; i++) { handleInsertClick(e, rowIndex); }
					} else { 
						handleInsertClick(e, rowIndex);
					}
				},
				(rowData) => RemoveRowHandler( 
					gridRef,
					SYS4100_R01List, 
					rowData,  // rowData는 renderIndexCell에서 Dto 형식으로 선언함
					setClickable, 
					removeSYS4100_R01List, 
					(valueIndex) => addDeletedSYS4100_R01List([valueIndex.toString()])
				),
			)
		),
        {
            headerName: '조직번호', colId: 'orgCd', field: 'orgCd', width: 100,
			editable: (params) => { return params.data?.inserted === true? true : false; }, // 해당 행이 추가된(신규) 행일 때만 편집 가능
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
        {
            headerName: '부서명', colId: 'orgNm', field: 'orgNm', width: 110, 
			editable: true,
			suppressClickEdit: false,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
		{
            headerName: '상위부서', colId: 'upOrgCd', field: 'upOrgCd', width: 110, 
			editable: true,
			suppressClickEdit: true,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
		{
            headerName: '상위부서명', colId: 'upOrgNm', field: 'upOrgNm', width: 100, 
			editable: false,
			suppressClickEdit: true,
			comparator: cellStringComparator,
        },
        {
            headerName: '순서', colId: 'displaySeq', field: 'displaySeq', width: 100, 
			editable: true,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
        },
		{
            headerName: '부서장', colId: 'orgAdmin', field: 'orgAdmin', width: 100, 
			editable: true,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
        {
            headerName: '부서장명', colId: 'orgAdminNm', field: 'orgAdminNm', width: 100, 
			editable: false, 
			comparator: cellStringComparator,
        },
		{
			headerName: '등록일', colId: 'createDt', field: 'createDt', width: 100,
			editable: (params) => params.data?.inserted === true, // 신규행에서만 true
			suppressClickEdit: true, // 에디터는 열지 않고 클릭만 전달
			valueFormatter: (p) => p.data?.createDt ? formatDate(new Date(p.data?.createDt)) : '',
			comparator: cellDateComparator,
        },
		{
            headerName: '통합팀', colId: 'mergeOrgCd', field: 'mergeOrgCd', width: 100, 
			editable: true,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			
        },
		{
            headerName: '통합팀명', colId: 'mergeOrgNm', field: 'mergeOrgNm', width: 100,
			editable: false,
        },
		{
            headerName: '통합일', colId: 'dueDt', field: 'dueDt', width: 100,
			editable: (params) => validateString(params.data?.mergeOrgNm ?? '') ? true : false, // 신규행에서만 true
			suppressClickEdit: true, // 에디터는 열지 않고 클릭만 전달
			valueFormatter: (params: any) => params.data?.dueDt ? formatDate(new Date(params.data?.dueDt)) : '',
			comparator: cellDateComparator,
        },
        {
            headerName: '사용여부', colId: 'useYn', field: 'useYn', width: 100,
			...getSwitchButtonColumnOptions('useYn', handleSwitchButtonHandler, headerNames_SYS4100_R01, aggridSwitchButtonWrapperStyle, true),
        },
		{
            headerName: '최상위여부', colId: 'topOrganiza', field: 'topOrganiza', width: 100, 
			...getSwitchButtonColumnOptions('topOrganiza', handleSwitchButtonHandler, headerNames_SYS4100_R01, aggridSwitchButtonWrapperStyle, true),
        },
		// ...IndexParameterCell(),
	], [deletedSYS4100_R01List, SYS4100_R01List, handleInsertClick, createCellClassRules]);

	
	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: ColDef<SYS4100_R01Dto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<SYS4100_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames: headerNames_SYS4100_R01,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, columnState, headerNames_SYS4100_R01]);

	const { settingCodeSearchModalProps } = useSYS4100_R01Modal({
		SYS4100_R01List,
		updateSYS4100_R01List,
		originalSYS4100_R01List,
		gridRef,
	});

	// cellChange 및 modal 설정
	const { handleCellChange } = useSYS4100_R01CellChange({
		updateSYS4100_R01List,
		settingCodeSearchModalProps,
	});

    const handleRowClick = useRowSelectionHandler({
        dataList: SYS4100_R01List,
        idKey: 'valueIndex',
        selectedList: selectedSYS4100_R01List,
        deletedList: deletedSYS4100_R01List,
        addSelected: addSelectedSYS4100_R01List,
        removeSelected: removeSelectedSYS4100_R01List,
        clickable, setClickable
    });

	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: SYS4100_R01List as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});

	const cellClickActions: CustomCellClickAction<SYS4100_R01Dto>[] = useMemo(() => [
		{
			validate: (field, data) => commonInputColumns.includes(field),
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
		{
			validate: (field, data) => field === 'dueDt',
			action: createCalendarCellClickHandler('dueDt', SYS4100_R01GridCalendarState, setSYS4100_R01GridCalendarState),
		},
		{
			validate: (field, data) => (field === 'createDt' && data.inserted === true),
			action: createCalendarCellClickHandler('createDt', SYS4100_R01GridCalendarState, setSYS4100_R01GridCalendarState),
		},
	], [SYS4100_R01GridCalendarState]);
	const handleCellClick = CreateCellClickHandler(SYS4100_R01List, 'valueIndex', cellClickActions);
	
	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<SYS4100_R01Dto>[] = useMemo(() => [
		{
			validate: (field, data) => field === 'upOrgCd' || field === 'upOrgNm',
			action: (e, foundData) => {
				// 편집 중인 값 또는 저장된 값 가져오기 (stopEditing 없이)
				const editingCells = gridRef.current?.api?.getEditingCells();
				let cellValue = '';
				
				if (editingCells && editingCells.length > 0) {
					// 현재 편집 중이면 편집 중인 값 가져오기
					const cellEditor = gridRef.current?.api?.getCellEditorInstances()[0];
					if (cellEditor && (cellEditor as any).getValue) {
						cellValue = (cellEditor as any).getValue() || '';
					}
				}
				
				settingCodeSearchModalProps('상위부서', cellValue, false, 'upOrgCd', foundData, foundData.valueIndex!);
			}
		},
		{
			validate: (field, data) => field === 'orgAdmin' || field === 'orgAdminNm',
			action: (e, foundData) => {
				// 편집 중인 값 또는 저장된 값 가져오기 (stopEditing 없이)
				const editingCells = gridRef.current?.api?.getEditingCells();
				let cellValue = '';
				
				if (editingCells && editingCells.length > 0) {
					// 현재 편집 중이면 편집 중인 값 가져오기
					const cellEditor = gridRef.current?.api?.getCellEditorInstances()[0];
					if (cellEditor && (cellEditor as any).getValue) {
						cellValue = (cellEditor as any).getValue() || '';
					}
				}
				
				settingCodeSearchModalProps('부서장', cellValue, false, 'orgAdmin', foundData, foundData.valueIndex!);
			}
		},
		{
			validate: (field, data) => field === 'mergeOrgCd' || field === 'mergeOrgNm',
			action: (e, foundData) => {
				// 편집 중인 값 또는 저장된 값 가져오기 (stopEditing 없이)
				const editingCells = gridRef.current?.api?.getEditingCells();
				let cellValue = '';
				
				if (editingCells && editingCells.length > 0) {
					// 현재 편집 중이면 편집 중인 값 가져오기
					const cellEditor = gridRef.current?.api?.getCellEditorInstances()[0];
					if (cellEditor && (cellEditor as any).getValue) {
						cellValue = (cellEditor as any).getValue() || '';
					}
				}
				
				settingCodeSearchModalProps('통합팀', cellValue, false, 'mergeOrgCd', foundData, foundData.valueIndex!);
			}
		},
	], [settingCodeSearchModalProps]);
	
	const handleCellDoubleClick = CreateCellDoubleClickHandler(SYS4100_R01List, 'valueIndex', cellDoubleClickActions);
	
	const handlePostSortWithDrag = useCallback((params: PostSortRowsParams<SYS4100_R01Dto>) => {
		// 드래그 중에는 postSortRows에 의한 강제 정렬을 막아 드래그가 눈에 보이도록 함
		if (isRowDraggingRef.current) return;
		postSortHandler(params, SYS4100_R01List, originalSYS4100_R01List, setSYS4100_R01List);
	}, [SYS4100_R01List, originalSYS4100_R01List]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '계',
		orgCd: SYS4100_R01List.length.toLocaleString() + "건",
		editable: false,
	}]), [SYS4100_R01List]);

    return (
		<GridContextWrapper<SYS4100_R01Dto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedSYS4100_R01List}
			defaultRowData={defaultSYS4100_R01Row}
			allSelected={allSelectedSYS4100_R01List}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<SYS4100_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
				columnDefs={columnDefs}		
				defaultColDef={cellCssDefaultColDef}

				rowClassRules={createRowClassRules<SYS4100_R01Dto>({
					idKey: 'valueIndex',
					selectedList: selectedSYS4100_R01List,
					deletedList: deletedSYS4100_R01List
				})}
				onRowClicked={handleRowClick}
				onCellClicked={handleCellClick}
				onCellDoubleClicked={handleCellDoubleClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={handleCellChange}
				pinnedBottomRowData={pinnedBottomRow}

				onPostSortRows={handlePostSortWithDrag} //onSortChanged 삭제, 커스텀 정렬 기능추가 
                // Grid_Select에 공통 로직 처리를 위한 props 전달
                columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
				setColumnState={setColumnState}				//컬럼 상태 저장
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
				
			/>

			<GridCalendar
				rowIndex={targetRowIndex!}
				targetField={targetField!}
				visible={pickerVisible}
				setVisible={(visible) => setSYS4100_R01GridCalendarState({ pickerVisible: visible })}
				value={targetDate}
				pickerPosition={pickerPosition}
				setPickerPosition={(position) => setSYS4100_R01GridCalendarState({ pickerPosition: position })}
				onClear={() => {
					if(targetField === 'createDt') {
						showWarning('등록일은 변경할 수 없습니다.');
					}else{
						if (targetRowIndex !== null && targetField) {
							const foundData = SYS4100_R01List[targetRowIndex];
							if (foundData) {
								updateSYS4100_R01List(foundData.valueIndex!, {
									...foundData,
									[targetField]: undefined,
								});
							}
						}
					}
				}}
				onChange={(date, rowIndex, targetField) => {
					if (rowIndex !== null && targetField) {
						const foundData = SYS4100_R01List[rowIndex];
						if (foundData) {
							updateSYS4100_R01List(foundData.valueIndex!, {
								...foundData,
								[targetField]: date ?? new Date(),
								isModified: true
							});
						}
					}
				}}
				onClickLoad={(rowIndex, targetField) => {
					const foundData = SYS4100_R01List[rowIndex];
					const date = foundData[targetField as keyof SYS4100_R01Dto] as Date;
					if(date) {
						setSYS4100_R01GridCalendarState({ targetDate: new Date(date) });
					}else{
						setSYS4100_R01GridCalendarState({ targetDate: new Date() });
					}
				}}
			/>
		</GridContextWrapper>
    )
}
